import { ChangeDetectionStrategy, ChangeDetectorRef, Component, model, output, ViewChild, ElementRef, AfterViewChecked, Inject, Optional, InjectionToken, Injector, OnInit, OnDestroy, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AcaUiModule } from '@aca/ui';
import { AcaFaIcon } from '@aca/ui';
import { 
  faCircleXmark, 
  faPaperPlane, 
  faUser,
  faSparkles,
  faDollarSign,
  faCar,
  faCreditCard,
  faBox,
  faCircleQuestion
} from '@fortawesome/pro-regular-svg-icons';
import { faRobot } from '@fortawesome/pro-solid-svg-icons';
import { TourService, TourStep } from '../services/tour.service';
import { TourContentRegistryService } from '../services/tour-content-registry.service';
import { ChatbotDataService, VehicleData, VehicleDataUpdate } from '../services/chatbot-data.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/**
 * Minimal contract for the optionally-injected tour content service.
 * Defined here (not in the rehash module) so shared-services has no hard
 * dependency on any MFE-specific module.
 */
export interface TourContentProvider {
  /** Returns the full application walkthrough as an ordered step list. */
  getApplicationTour(): TourStep[];
  /** Returns the steps for a named feature tour. */
  getFeatureTour(tourType: string): TourStep[];
}

/** Injection token for the optional tour content service. */
export const TOUR_CONTENT_SERVICE = new InjectionToken<TourContentProvider>('TourContentService');

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  dealFields?: {
    field: string;
    value: string;
    label: string;
  }[];
  tourTrigger?: boolean;
  tourType?: string;
}

@Component({
  selector: 'acadv-ai-chatbot',
  standalone: true,
  imports: [DatePipe, FormsModule, AcaUiModule],
  templateUrl: './ai-chatbot.component.html',
  styleUrls: ['./ai-chatbot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatbotComponent implements AfterViewChecked, OnInit, OnDestroy {
  /** Vehicle data passed from the parent or updated internally by the chatbot data service. */
  readonly vehicleData = model<VehicleData>();
  /** Emits field-level vehicle data updates for parent components to apply. */
  readonly updateVehicleData = output<VehicleDataUpdate>();
  @ViewChild('messagesContainer') messagesContainer?: ElementRef;

  isOpen = false;
  showWelcome = false;
  messages: Message[] = [];
  inputValue = '';
  isTyping = false;
  private shouldScrollToBottom = false;
  /** Optionally-injected tour content service; null when running outside a MFE that provides it. */
  tourContentService: TourContentProvider | null = null;
  private destroy$ = new Subject<void>();
  private welcomePopupShown = false; // Track if welcome has been shown this component instance
  private readonly cdr = inject(ChangeDetectorRef);

  icons = {
    robot: new AcaFaIcon(faRobot),
    close: new AcaFaIcon(faCircleXmark),
    send: new AcaFaIcon(faPaperPlane),
    user: new AcaFaIcon(faUser),
    sparkles: new AcaFaIcon(faSparkles),
    dollar: new AcaFaIcon(faDollarSign),
    car: new AcaFaIcon(faCar),
    creditCard: new AcaFaIcon(faCreditCard),
    box: new AcaFaIcon(faBox),
    help: new AcaFaIcon(faCircleQuestion)
  };

  get hasMessages(): boolean {
    return this.messages.length > 1; // More than just the initial welcome message
  }

  constructor(
    private tourService: TourService,
    private router: Router,
    private tourRegistry: TourContentRegistryService,
    private chatbotDataService: ChatbotDataService,
    private injector: Injector,
    @Optional() @Inject(TOUR_CONTENT_SERVICE) injectedService?: TourContentProvider
  ) {
    this.tourContentService = injectedService || null;
  }

  ngOnInit() {
    // Initialize with welcome message FIRST
    this.initializeChat();

    // Subscribe to router events to reset welcome popup on navigation
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        console.log('[Chatbot] Navigation detected, resetting welcome popup');
        // Hide any currently showing welcome popup
        this.showWelcome = false;
        // Reset the flag and schedule for the new page
        this.welcomePopupShown = false;
        this.scheduleWelcomePopup();
        this.cdr.markForCheck();
      });

    // Subscribe to tour service registry changes
    this.tourRegistry.tourService$
      .pipe(takeUntil(this.destroy$))
      .subscribe(service => {
        this.tourContentService = service;
        console.log('[Chatbot] Tour service changed from registry:', !!service, 'Route:', this.router.url);
        
        // Reinitialize chat to update suggestions when tour availability changes
        // Only if chat is not open AND has no conversation history
        if (!this.isOpen && !this.hasMessages) {
          this.initializeChat();
        }

        // Schedule welcome popup when tour service becomes available
        if (service && !this.welcomePopupShown) {
          console.log('[Chatbot] Tour service now available, scheduling welcome popup');
          this.scheduleWelcomePopup();
        }
        this.cdr.markForCheck();
      });

    // Mirror vehicle data signal changes from the service into the component model.
    // toObservable converts the signal to a stream so that vehicleData.set() (a
    // state mutation) runs inside a subscription callback rather than inside an
    // effect() — direct or indirect state mutation inside effect() is prohibited
    // by the Angular compliance rule. The injector option is required because
    // this runs outside the constructor injection context.
    toObservable(this.chatbotDataService.vehicleData, { injector: this.injector })
      .pipe(
        filter((data): data is VehicleData => data != null),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        console.log('[Chatbot] Received vehicle data update:', data);
        this.vehicleData.set(data);
      });

    // Schedule initial welcome popup
    this.scheduleWelcomePopup();
  }

  private scheduleWelcomePopup() {
    // Check if welcome message should be shown
    // Show once per page/navigation if: 1) hasn't been shown since last navigation, 2) chat hasn't been initiated, 3) chat isn't open, 4) on a rehash detail page (has tour service)
    console.log('[Chatbot] Welcome popup check:', {
      welcomePopupShown: this.welcomePopupShown,
      hasMessages: this.hasMessages,
      messagesLength: this.messages.length,
      isOpen: this.isOpen,
      hasTourService: !!this.tourContentService,
      route: this.router.url
    });
    
    // Only show welcome popup on rehash detail pages (where tour service is available)
    if (!this.tourContentService) {
      console.log('[Chatbot] NOT scheduling welcome popup - not on rehash detail page');
      return;
    }
    
    if (!this.welcomePopupShown && !this.hasMessages) {
      console.log('[Chatbot] Scheduling welcome popup to show in 2 seconds');
      // Show welcome popup after 2 seconds
      setTimeout(() => {
        console.log('[Chatbot] Checking if should show welcome popup now:', {
          isOpen: this.isOpen,
          hasMessages: this.hasMessages,
          welcomePopupShown: this.welcomePopupShown,
          hasTourService: !!this.tourContentService
        });
        if (!this.isOpen && !this.hasMessages && !this.welcomePopupShown && this.tourContentService) {
          console.log('[Chatbot] Showing welcome popup');
          this.showWelcome = true;
          this.welcomePopupShown = true; // Mark as shown for this component instance
          this.cdr.markForCheck();
          
          // Auto-dismiss welcome popup after 10 seconds
          setTimeout(() => {
            console.log('[Chatbot] Auto-dismissing welcome popup');
            this.showWelcome = false;
            this.cdr.markForCheck();
          }, 10000);
        } else {
          console.log('[Chatbot] NOT showing welcome popup - conditions not met');
        }
      }, 2000);
    } else {
      console.log('[Chatbot] NOT scheduling welcome popup - already shown or has messages');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  scrollToBottom() {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  initializeChat() {
    const vehicleInfo = this.vehicleData()
      ? `${this.vehicleData()?.year} ${this.vehicleData()?.make} ${this.vehicleData()?.model}`
      : 'your vehicle';

    // Check if tour feature is available
    const hasTourFeature = !!this.tourContentService;
    
    const suggestions = hasTourFeature
      ? [
          'Give me a tour of this application',
          'Minimum fee structure',
          'Trade stipulation removal',
          'Zero down payment options'
        ]
      : [
          'Minimum fee structure',
          'Trade stipulation removal',
          'Zero down payment options',
          'Alternative vehicle recommendations'
        ];
    
    this.messages = [{
      id: '1',
      type: 'bot',
      content: `Welcome to ACA AI Assistant. I've analyzed ${vehicleInfo} using current market data, comparable sales analysis, and ACA's optimization best practices. What deal optimization would you like me to focus on?`,
      timestamp: new Date(),
      suggestions: suggestions
    }];
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
    this.showWelcome = false;
    
    // Scroll to bottom when opening chat (if there's conversation history)
    // Otherwise scroll to top to see full welcome message
    if (this.isOpen) {
      if (this.hasMessages) {
        this.shouldScrollToBottom = true;
      } else {
        // First time opening - scroll to top to see welcome message
        setTimeout(() => {
          if (this.messagesContainer) {
            this.messagesContainer.nativeElement.scrollTop = 0;
          }
        }, 0);
      }
    }
  }

  closeChat() {
    this.isOpen = false;
  }

  closeWelcome() {
    this.showWelcome = false;
  }

  handleGetStarted() {
    this.showWelcome = false;
    this.isOpen = true;
    
    // First time opening from welcome popup - scroll to top to see full message
    setTimeout(() => {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = 0;
      }
    }, 0);
  }

  handleSendMessage() {
    if (!this.inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: this.inputValue,
      timestamp: new Date()
    };

    this.messages = [...this.messages, userMessage];
    this.shouldScrollToBottom = true;
    const userInput = this.inputValue;
    this.inputValue = '';
    this.isTyping = true;
    this.cdr.markForCheck();

    // Simulate typing delay
    setTimeout(() => {
      const botResponse = this.generateBotResponse(userInput);
      this.messages = [...this.messages, botResponse];
      this.isTyping = false;
      this.shouldScrollToBottom = true;
      this.cdr.markForCheck();
    }, 1500);
  }

  handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.handleSendMessage();
    }
  }

  handleSuggestionClick(suggestion: string) {
    this.inputValue = suggestion;
    setTimeout(() => this.handleSendMessage(), 100);
  }

  applyDealFields(dealFields: NonNullable<Message['dealFields']>): void {
    if (!dealFields) return;

    const updates: VehicleDataUpdate = {};
    dealFields.forEach(field => {
      updates[field.field] = field.value;
    });

    // Emit to both the direct output (for backward compatibility if chatbot is in component)
    // and to the service (for when chatbot is in shell)
    this.updateVehicleData.emit(updates);
    this.chatbotDataService.emitUpdate(updates);

    const confirmMessage: Message = {
      id: Date.now().toString(),
      type: 'bot',
      content: 'Updates applied successfully. The modified values are now reflected in your Deal Structure panel for review.',
      timestamp: new Date(),
      suggestions: [
        'Show me more options',
        'Optimize further',
        'Compare with other deals',
        'I\'m satisfied with this deal'
      ]
    };

    this.messages = [...this.messages, confirmMessage];
    this.cdr.markForCheck();
    this.closeChat();
  }

  generateBotResponse(userMessage: string): Message {
    const lowerMessage = userMessage.toLowerCase();
    const currentSalesPrice = this.vehicleData()?.salesPrice || 22800;
    const currentCashDown = this.vehicleData()?.cashDown || 0;
    const currentNetTrade = this.vehicleData()?.netTrade || 0;
    
    // Use previous (baseline) values for threshold calculation
    const previousSalesPrice = this.vehicleData()?.previousSalesPrice || currentSalesPrice;
    const previousCashDown = this.vehicleData()?.previousCashDown || currentCashDown;
    const previousNetTrade = this.vehicleData()?.previousNetTrade || currentNetTrade;
    
    const MINIMUM_CHANGE = 250;

    // Check for tour-related queries
    if (this.isTourRequest(lowerMessage)) {
      return this.generateTourResponse(lowerMessage);
    }

    /**
     * Helper function to ensure value changes meet minimum threshold from baseline
     */
    const ensureMinimumChange = (baselineValue: number, targetValue: number, preferLower = true): number => {
      const diff = Math.abs(targetValue - baselineValue);
      if (diff >= MINIMUM_CHANGE) {
        return targetValue;
      }
      // If change is too small, adjust to meet minimum from baseline
      return preferLower 
        ? baselineValue - MINIMUM_CHANGE 
        : baselineValue + MINIMUM_CHANGE;
    };

    if (lowerMessage.includes('payment') || lowerMessage.includes('lower')) {
      let optimizedPrice = Math.round(currentSalesPrice * 0.94);
      optimizedPrice = ensureMinimumChange(previousSalesPrice, optimizedPrice, true);
      
      const newCashDown = Math.max(Math.round(optimizedPrice * 0.15), currentCashDown);

      return {
        id: Date.now().toString(),
        type: 'bot',
        content: `Analysis complete. I've identified optimal payment reduction strategies for your vehicle. Based on current market analysis and ACA optimization protocols, here are my professional recommendations:`,
        timestamp: new Date(),
        suggestions: [
          'Show me other financing options',
          'What if customer has trade-in?',
          'Include protection products',
          'Compare with similar vehicles'
        ],
        dealFields: [
          { field: 'salesPrice', value: optimizedPrice.toString(), label: 'Optimized Sales Price' },
          { field: 'cashDown', value: newCashDown.toString(), label: 'Recommended Down Payment' }
        ]
      };
    }

    if (lowerMessage.includes('lowest') && lowerMessage.includes('fee')) {
      let lowestPrice = Math.round(previousSalesPrice * 0.89);
      lowestPrice = ensureMinimumChange(previousSalesPrice, lowestPrice, true);
      
      const minimalDown = Math.max(Math.round(lowestPrice * 0.10), 0);

      return {
        id: Date.now().toString(),
        type: 'bot',
        content: `Minimum pricing analysis complete for your vehicle. These represent the most competitive rates while maintaining acceptable profit margins:`,
        timestamp: new Date(),
        suggestions: [
          'Can we go even lower?',
          'What\'s dealer\'s actual cost?',
          'Include this in payment calculation',
          'Compare with KBB values'
        ],
        dealFields: [
          { field: 'salesPrice', value: lowestPrice.toString(), label: 'Absolute Minimum Price' },
          { field: 'cashDown', value: minimalDown.toString(), label: 'Minimal Down Payment' }
        ]
      };
    }

    if (lowerMessage.includes('no cash down') || (lowerMessage.includes('no') && lowerMessage.includes('down'))) {
      let adjustedPrice = previousSalesPrice;
      
      // If cash down is currently > 0, we need to increase sales price to compensate
      if (currentCashDown > MINIMUM_CHANGE) {
        adjustedPrice = previousSalesPrice + Math.max(Math.round(currentCashDown * 0.5), MINIMUM_CHANGE);
      } else {
        // If already low/no cash down, still need minimum change from baseline
        adjustedPrice = ensureMinimumChange(previousSalesPrice, Math.round(previousSalesPrice * 1.02), false);
      }

      return {
        id: Date.now().toString(),
        type: 'bot',
        content: `Zero down payment structure available for your vehicle. Here's the recommended financing approach to maximize accessibility:`,
        timestamp: new Date(),
        suggestions: [
          'What\'s the monthly payment?',
          'Include taxes and fees?',
          'Add protection products?',
          'Compare with down payment options'
        ],
        dealFields: [
          { field: 'salesPrice', value: adjustedPrice.toString(), label: 'No Down Payment Price' },
          { field: 'cashDown', value: '0', label: 'Cash Down Payment' }
        ]
      };
    }

    // Default response
    const hasTourFeature = !!this.tourContentService;
    const defaultSuggestions = hasTourFeature
      ? [
          'Lowest Fee Possible',
          'No Cash Down',
          'Remove Stip for Trade',
          'Give me a tour of this application'
        ]
      : [
          'Lowest Fee Possible',
          'No Cash Down',
          'Remove Stip for Trade',
          'Other cars that fit budget'
        ];
    
    return {
      id: Date.now().toString(),
      type: 'bot',
      content: `Deal optimization ready for your vehicle. I can provide pricing analysis, payment structuring, trade evaluation, or alternative vehicle recommendations. Please specify which optimization area you'd like me to prioritize.`,
      timestamp: new Date(),
      suggestions: defaultSuggestions
    };
  }

  /**
   * Check if the user message is requesting a tour
   */
  private isTourRequest(message: string): boolean {
    const tourKeywords = [
      'tour',
      'guide',
      'tutorial',
      'how does this work',
      'how does this app work',
      'how does the app work',
      'how does this application work',
      'how does the application work',
      'what can i do',
      'what can i do here',
      'what can i do in this app',
      'what can i do in this application',
      'show me around',
      'walk me through',
      'explain this app',
      'explain this application',
      'explain the features',
      'how to use',
      'help me understand',
      'show me how to',
      'demo'
    ];

    return tourKeywords.some(keyword => message.includes(keyword));
  }

  /**
   * Generate response for tour-related requests
   */
  private generateTourResponse(message: string): Message {
    // Check if tour service is actually available
    if (!this.tourContentService) {
      return {
        id: Date.now().toString(),
        type: 'bot',
        content: `I'd love to give you a tour! However, the interactive tour is currently only available on the Deal Rehash detail pages. Please navigate to a specific deal in the Rehash section to access the guided tour feature.`,
        timestamp: new Date(),
        suggestions: [
          'How does rehash work?',
          'What features are available?',
          'I have another question'
        ]
      };
    }

    let tourType = 'full';
    let content = '';
    let suggestions = [
      'Show me structure features',
      'Explain analytics',
      'How do I manage vehicles?',
      'What are stipulations?'
    ];

    // Determine specific tour type based on message content
    if (message.includes('structure') || message.includes('fields') || message.includes('edit')) {
      tourType = 'structure';
      content = 'I\'ll show you how to work with the deal structure, edit fields, lock/unlock values, and use undo/redo features. Ready to start?';
    } else if (message.includes('analytics') || message.includes('scenario') || message.includes('suggestion')) {
      tourType = 'analytics';
      content = 'I\'ll explain how the suggested scenarios feature works and how it can help you optimize deals. Ready to start?';
    } else if (message.includes('vehicle') || message.includes('collateral') || message.includes('car')) {
      tourType = 'vehicle';
      content = 'I\'ll show you how to view vehicle information, upload images, and switch vehicles. Ready to start?';
    } else if (message.includes('stip') || message.includes('document') || message.includes('upload')) {
      tourType = 'stipulations';
      content = 'I\'ll explain stipulations - what they are, how to view them, and how to upload required documents. Ready to start?';
    } else if (message.includes('action') || message.includes('recalculate') || message.includes('submit')) {
      tourType = 'actions';
      content = 'I\'ll walk you through the actions panel - recalculating, resetting, submitting, and saving deals. Ready to start?';
    } else {
      // Full application tour
      content = 'I\'ll give you a complete tour of the Deal Rehash application, covering all features and functionality. This will take about 3-5 minutes. Ready to start?';
      suggestions = [
        'Yes, start the tour!',
        'Show me quick tips instead',
        'Just explain structure features',
        'Maybe later'
      ];
    }

    return {
      id: Date.now().toString(),
      type: 'bot',
      content: content,
      timestamp: new Date(),
      suggestions: suggestions,
      tourTrigger: true,
      tourType: tourType
    };
  }

  /**
   * Start the tour with the tour service
   */
  startTour(tourType?: string): void {
    // Get tour steps from the tour content service if available
    if (!this.tourContentService) {
      // Tour feature not available (shouldn't happen as button is hidden)
      const errorMessage: Message = {
        id: Date.now().toString(),
        type: 'bot',
        content: 'I apologize, but the tour feature is not available at the moment. However, I can still help you with deal optimization, pricing analysis, and other questions.',
        timestamp: new Date(),
        suggestions: [
          'Lowest Fee Possible',
          'No Cash Down',
          'Optimize this deal'
        ]
      };
      this.messages = [...this.messages, errorMessage];
      this.cdr.markForCheck();
      return;
    }

    let steps;
    try {
      if (tourType === 'full' || !tourType) {
        steps = this.tourContentService.getApplicationTour();
      } else {
        steps = this.tourContentService.getFeatureTour(tourType);
      }
    } catch (error) {
      console.error('[Tour] Error getting tour steps:', error);
      return;
    }
    
    if (steps && steps.length > 0) {
      // Start the tour after a small delay to ensure UI is ready
      setTimeout(() => {
        this.tourService.startTour(steps);
      }, 100);

      // Add confirmation message
      const confirmMessage: Message = {
        id: Date.now().toString(),
        type: 'bot',
        content: 'Tour started! Follow the guided steps to learn about the application. You can exit the tour anytime by clicking the close button.',
        timestamp: new Date(),
        suggestions: [
          'I have another question',
          'Optimize this deal',
          'Show me lowest fee'
        ]
      };

      this.messages = [...this.messages, confirmMessage];
      this.cdr.markForCheck();
      
      // Close the chatbot after starting the tour
      setTimeout(() => {
        this.closeChat();
      }, 1000);
    }
  }
}

